import { PlusOutlined } from "@ant-design/icons";
import { FaCube } from "react-icons/fa";
import {
  Button,
  Card,
  Divider,
  Input,
  Space,
  Switch,
  Typography,
  AutoComplete,
  Spin,
  Row,
  Col,
} from "antd";
import "../styles/signal-fix-components.css";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CiExport, CiSearch } from "react-icons/ci";
import { LuSettings2 } from "react-icons/lu";
import { MdOutlineDirectionsCar } from "react-icons/md";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import CarIcon from "../assets/icons/car_icon.png"
import PositionIcon from "../assets/icons/position_icon.png"
import LengthIcon from "../assets/icons/length_icon.png"
import { MdOutlineFileDownload } from "react-icons/md";
import { Car } from "lucide-react";
import { fetchClientJSON } from "../../services/clientDataService";
import { getClientType } from "../../services/clientConfig";
import { trafficSigns } from "../data/fakeSignals";
const { Text, Title } = Typography;

const loadSignsMap = () => {
  try {
    const context = require.context("../assets/textures", false, /\.png$/);
    return context.keys().reduce((accumulator, key) => {
      const fileName = key.replace("./", "");
      accumulator[fileName] = context(key);
      return accumulator;
    }, {});
  } catch (error) {
    console.error("Unable to load sign assets for signal filters:", error);
    return {};
  }
};

const signsMap = loadSignsMap();

const fakeSignalTypeMetadata = trafficSigns.reduce((accumulator, sign) => {
  if (!sign?.type || accumulator[sign.type]) {
    return accumulator;
  }

  const iconUrl = Array.isArray(sign.image_url)
    ? sign.image_url.map((asset) => (asset?.default ?? asset)).find(Boolean)
    : sign.image_url?.default ?? sign.image_url ?? null;

  accumulator[sign.type] = {
    iconUrl,
    label: sign.description || sign.type,
  };

  return accumulator;
}, {});

const SignalFixExtras = ({
  signs = [],
  setAddNew,
  setFilteredSigns,
  setFilterActivated,
  onLocationSearch, // optional live search hook (kept for compatibility)
  onLocationSelect, // optional select callback: receives {label, coordinates, data}
  showCarPath = false,
  onToggleCarPath,
  carPathGeoJson = null,
  show3DMarkers = false,
  onToggle3DMarkers,
  showSegmentiRms = false,
  onToggleSegmentiRms,
  segmentiRmsGeoJson = null,
}) => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [availableSegnaliTypes, setAvailableSegnaliTypes] = useState([]);
  const [availableSegnaleticaTypes, setAvailableSegnaleticaTypes] = useState([]);
  const [showFilterCard, setShowFilterCard] = useState(false);
  const [segnaliFilterOptions, setSegnaliFilterOptions] = useState({});
  const [segnaleticaFilterOptions, setSegnaleticaFilterOptions] = useState({});
  const [activeTab, setActiveTab] = useState('segnali'); // 'segnali' or 'segnaletica'
  const [cardSearchTerm, setCardSearchTerm] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCarPathCard, setShowCarPathCard] = useState(false);
  const [carPathDistance, setCarPathDistance] = useState(null);
  const [carPathLocation, setCarPathLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const filterRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Get types for Segnali (where Segnaletica is false or undefined)
    const segnaliSigns = signs.filter((sign) => !sign.Segnaletica);
    const segnaliTypes = [...new Set(segnaliSigns.map((sign) => sign.type))];
    setAvailableSegnaliTypes(segnaliTypes);

    // Get types for Segnaletica (where Segnaletica is true)
    const segnaleticaSigns = signs.filter((sign) => sign.Segnaletica === true);
    const segnaleticaTypes = [...new Set(segnaleticaSigns.map((sign) => sign.type))];
    setAvailableSegnaleticaTypes(segnaleticaTypes);

    // Initialize all as enabled
    const initialSegnaliOptions = {};
    segnaliTypes.forEach((type) => {
      initialSegnaliOptions[type] = true;
    });
    setSegnaliFilterOptions(initialSegnaliOptions);

    const initialSegnaleticaOptions = {};
    segnaleticaTypes.forEach((type) => {
      initialSegnaleticaOptions[type] = true;
    });
    setSegnaleticaFilterOptions(initialSegnaleticaOptions);

    // Show all signs initially
    if (setFilteredSigns) setFilteredSigns(signs);
    setSelectedTypes([]);
  }, [signs, setFilteredSigns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterCard(false);
      }
      // Car path card only closes via close button or toggling the car button
      // No click-outside behavior for car path card
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load car path info (location and distance) from client JSON file
  useEffect(() => {
    const clientType = getClientType();
    setLocationLoading(true);
    fetchClientJSON(clientType, 'signal_fix/car_path_info.json')
      .then(data => {
        if (data) {
          setCarPathLocation(data.location || null);
          setCarPathDistance(data.distance ?? null);
        }
      })
      .catch(error => {
        console.error("Error fetching car path info:", error);
        setCarPathLocation(t('signalFix.unknownLocation'));
      })
      .finally(() => {
        setLocationLoading(false);
      });
  }, []);

  // Helper function to delay between requests
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchAddress = async (lat, lng, retryCount = 0) => {
    const maxRetries = 2;
    const baseDelay = 1000; // 1 second base delay

    try {
      // Add delay to avoid rate limiting (Nominatim has rate limits)
      if (retryCount > 0) {
        await delay(baseDelay * retryCount);
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        {
          headers: {
            "User-Agent": "SemafixApp/1.0",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data?.display_name) {
        return data.display_name;
      } else if (data?.error) {
        console.warn(`Nominatim API error for ${lat},${lng}:`, data.error);
        return t('signalFix.addressNotAvailable');
      } else {
        return t('signalFix.addressNotAvailable');
      }
    } catch (error) {
      console.error(
        `Error fetching address for ${lat},${lng} (attempt ${retryCount + 1}):`,
        error
      );

      // Retry logic
      if (retryCount < maxRetries) {
        return fetchAddress(lat, lng, retryCount + 1);
      }

      return "Indirizzo non disponibile";
    }
  };

  const handleExcelDownload = async () => {
    setExportLoading(true);

    try {
      const dataWithAddresses = [...signs];

      // Find signs that need address fetching
      const signsNeedingAddresses = dataWithAddresses
        .map((sign, index) => ({ sign, index }))
        .filter(
          ({ sign }) =>
            !sign.indirizzo || sign.indirizzo === "Indirizzo non disponibile"
        );


      // Process addresses in batches to avoid overwhelming the API
      const batchSize = 5; // Process 5 at a time
      const batches = [];
      for (let i = 0; i < signsNeedingAddresses.length; i += batchSize) {
        batches.push(signsNeedingAddresses.slice(i, i + batchSize));
      }

      // Process each batch sequentially with delays
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Process items in the current batch concurrently
        const batchPromises = batch.map(async ({ sign, index }) => {
          const address = await fetchAddress(sign.lat, sign.lng);
          return { index, address };
        });

        const batchResults = await Promise.all(batchPromises);

        // Update addresses for this batch
        batchResults.forEach(({ index, address }) => {
          dataWithAddresses[index].indirizzo = address;
        });

        // Add delay between batches to respect rate limits
        if (batchIndex < batches.length - 1) {
          await delay(500); // 500ms delay between batches
        }
      }

      exportToExcel(dataWithAddresses);
    } catch (error) {
      console.error("Error during export:", error);
      // Even if address fetching fails, try to export with what we have
      exportToExcel(signs);
    } finally {
      setExportLoading(false);
    }
  };

  const exportToExcel = (signals) => {
    const headers = [
      "ID",
      "External ID",
      "Tipo",
      "Descrizione",
      "Latitudine",
      "Longitudine",
      "Indirizzo",
      "Ultima Ispezione",
      "Condizione",
      "Foto",
    ];

    const rows = signals.map((s) => [
      s.id ?? "",
      s.external_id ?? "",
      s.type ?? "",
      s.description ?? "",
      s.lat ?? "",
      s.lng ?? "",
      s.indirizzo ?? s.street ?? "",
      s.lastInspection ?? s.last_inspection ?? "",
      s.condition ?? "",
      s.image_url ?? s.photo_url ?? "",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Column widths for readability
    worksheet["!cols"] = [
      { wch: 10 }, // ID
      { wch: 16 }, // External ID
      { wch: 18 }, // Tipo
      { wch: 30 }, // Descrizione
      { wch: 11 }, // Latitudine
      { wch: 11 }, // Longitudine
      { wch: 30 }, // Indirizzo
      { wch: 18 }, // Ultima Ispezione
      { wch: 16 }, // Condizione
      { wch: 28 }, // Foto
    ];

    // Add auto filter across the header row
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: range.e.r, c: range.e.c },
      }),
    };

    // Freeze header row if supported by the library version
    // Some builds support !freeze; if not available it will be ignored safely
    worksheet["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomLeft",
    };

    // Attempt simple header styling (may be ignored in community builds)
    headers.forEach((_, colIdx) => {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      const cell = worksheet[cellAddr];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "355A0A" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Segnali");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `segnali_${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const handleFilterToggle = (type, enabled, category) => {
    // Update the appropriate filter options
    if (category === 'segnali') {
      const newOptions = { ...segnaliFilterOptions, [type]: enabled };
      setSegnaliFilterOptions(newOptions);
    } else {
      const newOptions = { ...segnaleticaFilterOptions, [type]: enabled };
      setSegnaleticaFilterOptions(newOptions);
    }
    setFilterActivated(true);

    // Apply combined filters
    applyFilters(
      category === 'segnali' ? { ...segnaliFilterOptions, [type]: enabled } : segnaliFilterOptions,
      category === 'segnaletica' ? { ...segnaleticaFilterOptions, [type]: enabled } : segnaleticaFilterOptions
    );
  };

  const applyFilters = (segnaliOptions, segnaleticaOptions) => {
    const enabledSegnaliTypes = Object.keys(segnaliOptions).filter(key => segnaliOptions[key]);
    const enabledSegnaleticaTypes = Object.keys(segnaleticaOptions).filter(key => segnaleticaOptions[key]);

    // Filter 2D Segnali
    const filteredSegnali = signs.filter(sign =>
      !sign.Segnaletica && enabledSegnaliTypes.includes(sign.type)
    );

    // Filter 2D Segnaletica
    const filteredSegnaletica = signs.filter(sign =>
      sign.Segnaletica === true && enabledSegnaleticaTypes.includes(sign.type)
    );

    // Combine both for map display
    const allFiltered = [...filteredSegnali, ...filteredSegnaletica];
    if (setFilteredSigns) setFilteredSigns(allFiltered);

  };

  const handleSelectAll = (category) => {
    if (category === 'segnali') {
      const allEnabled = {};
      availableSegnaliTypes.forEach((type) => {
        allEnabled[type] = true;
      });
      setSegnaliFilterOptions(allEnabled);
      applyFilters(allEnabled, segnaleticaFilterOptions);
    } else {
      const allEnabled = {};
      availableSegnaleticaTypes.forEach((type) => {
        allEnabled[type] = true;
      });
      setSegnaleticaFilterOptions(allEnabled);
      applyFilters(segnaliFilterOptions, allEnabled);
    }
    setFilterActivated(false);
  };

  const handleDeselectAll = (category) => {
    if (category === 'segnali') {
      const allDisabled = {};
      availableSegnaliTypes.forEach((type) => {
        allDisabled[type] = false;
      });
      setSegnaliFilterOptions(allDisabled);
      applyFilters(allDisabled, segnaleticaFilterOptions);
    } else {
      const allDisabled = {};
      availableSegnaleticaTypes.forEach((type) => {
        allDisabled[type] = false;
      });
      setSegnaleticaFilterOptions(allDisabled);
      applyFilters(segnaliFilterOptions, allDisabled);
    }
    setFilterActivated(true);
  };

  const getSignTypeData = (type, category) => {
    const normalizedCategory = category === 'segnaletica'
      ? 'segnaletica'
      : category === 'segnali'
        ? 'segnali'
        : null;

    const signsByCategory = normalizedCategory === 'segnaletica'
      ? signs.filter((sign) => sign.Segnaletica === true)
      : normalizedCategory === 'segnali'
        ? signs.filter((sign) => !sign.Segnaletica)
        : signs;

    const count = signsByCategory.filter((sign) => sign.type === type).length;
    const metadata = fakeSignalTypeMetadata[type];

    // Try to match the sign type with icons from Assets/signs folder
    // Convert hyphens to underscores to match file naming convention
    const typeWithUnderscores = type.replace(/-/g, '-').toLowerCase().replace(/\//g, "_");
    const signFileName = `${typeWithUnderscores}.png`;
    const iconFromSigns = signsMap[signFileName];

    const iconUrl = iconFromSigns ?? metadata?.iconUrl;
    const value = metadata?.label || type;

    return {
      iconUrl,
      value,
      count,
    };
  };

  const typesMatchingSearch = (list, category) => {
    if (!cardSearchTerm.trim()) return list;
    const term = cardSearchTerm.toLowerCase();
    return list.filter((type) => {
      const data = getSignTypeData(type, category);
      return (
        type.toLowerCase().includes(term) ||
        (data?.value || "").toLowerCase().includes(term)
      );
    });
  };

  // --- Location search (Nominatim) ---
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSearchOptions([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5`
      );
      const data = await response.json();
      const options = data.map((item) => ({
        value: item.display_name,
        label: (
          <div className="location-suggest-item">
            <div className="suggestion-title">
              {item.display_name.split(",")[0]}
            </div>
            <div className="suggestion-subtitle">
              {item.display_name.split(",").slice(1).join(",").trim()}
            </div>
          </div>
        ),
        coordinates: [parseFloat(item.lat), parseFloat(item.lon)],
        data: item,
      }));
      setSearchOptions(options);
    } catch (error) {
      console.error("Error searching location:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleLocationSearch = (value) => {
    setLocationQuery(value);
    searchLocation(value);
    if (onLocationSearch) onLocationSearch(value);
  };

  const handleLocationSelect = (value, option) => {
    if (onLocationSelect && option) {
      onLocationSelect({
        label: value,
        coordinates: option.coordinates,
        data: option.data,
      });
    }
  };

  return (
    <div className="signal-fix-toolbar">
      {/* Left side: Filter */}
      <div className="signal-fix-toolbar-left" ref={filterRef}>
        <div className="filter-input-container">
          <AutoComplete
            value={locationQuery}
            options={searchOptions}
            onSelect={handleLocationSelect}
            onSearch={handleLocationSearch}
            className="map-search-autocomplete"
            notFoundContent={searchLoading ? <Spin size="small" /> : null}
            dropdownClassName="location-suggest-dropdown"
          >
            <Input
              placeholder={t('signalFix.searchLocation')}
              prefix={<CiSearch className="search-icon" />}
              suffix={
                <button
                  type="button"
                  className="filter-settings-btn"
                  aria-label="Apri filtro"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterCard(!showFilterCard);
                  }}
                >
                  <LuSettings2 className="settings-icon" />
                </button>
              }
              className="filter-input"
              allowClear
            />
          </AutoComplete>

          {showFilterCard && (
            <Card className="filter-card" bodyStyle={{ padding: 0 }}>
              {/* Card Title */}
              <div
                style={{
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  <b> {t('signalFix.filterTitle')}</b>
                </Title>
                <button
                  type="button"
                  className="filter-card-close"
                  aria-label="Chiudi filtro"
                  onClick={() => setShowFilterCard(false)}
                >
                  ×
                </button>
              </div>

              {/* 3D Markers Toggle - At the top */}
              <div>
                <div className="filter-option-item-map" >
                  <div className="filter-option-content">
                    <span className="filter-option-icon">
                      <FaCube style={{ fontSize: 20, color: show3DMarkers ? '#A1E340' : '#999' }} />
                    </span>
                    <Text className="filter-option-text" style={{ fontWeight: 600 }}>
                      {t('signalFix.show3DMarkers') || 'Show 3D Point Cloud Markers'}
                    </Text>
                  </div>
                  <Switch
                    checked={show3DMarkers}
                    onChange={(checked) => onToggle3DMarkers && onToggle3DMarkers(checked)}
                    className="filter-switch"
                  />
                </div>

                {/* Segmenti RMS Toggle - Below 3D Markers */}
                <div className="filter-option-item-map">
                  <div className="filter-option-content">
                    <span className="filter-option-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7L21 7" stroke={showSegmentiRms ? '#ffaa00' : '#999'} strokeWidth="2" strokeLinecap="round"/>
                        <path d="M3 12L21 12" stroke={showSegmentiRms ? '#ffaa00' : '#999'} strokeWidth="2" strokeLinecap="round"/>
                        <path d="M3 17L21 17" stroke={showSegmentiRms ? '#ffaa00' : '#999'} strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="7" cy="7" r="2" fill={showSegmentiRms ? '#ffaa00' : '#999'}/>
                        <circle cx="12" cy="12" r="2" fill={showSegmentiRms ? '#ffaa00' : '#999'}/>
                        <circle cx="17" cy="17" r="2" fill={showSegmentiRms ? '#ffaa00' : '#999'}/>
                      </svg>
                    </span>
                    <Text className="filter-option-text" style={{ fontWeight: 600 }}>
                      {t('signalFix.showSegmentiRms') || 'Show RMS Segments'}
                    </Text>
                  </div>
                  <Switch
                    checked={showSegmentiRms}
                    onChange={(checked) => onToggleSegmentiRms && onToggleSegmentiRms(checked)}
                    className="filter-switch"
                  />
                </div>
              </div>

              {/* Switch Button */}
              <div className="filter-card-header" style={{ padding: "0 12px" }}>
                <Switch
                  checked={activeTab === 'segnali'}
                  onChange={(checked) => setActiveTab(checked ? 'segnali' : 'segnaletica')}
                />
              </div>

              {/* Search Input */}
              <div
                className="filter-search-container"
                style={{ padding: "12px" }}
              >
                <Input
                  placeholder={t('signalFix.filterBySignalType')}
                  prefix={<CiSearch />}
                  className="filter-search-input"
                  value={cardSearchTerm}
                  onChange={(e) => setCardSearchTerm(e.target.value)}
                />
              </div>

              {/* Filter Options */}
              <div
                className="filter-options-container"
                style={{ padding: "0 12px 12px" }}
              >
                {activeTab === 'segnali' ? (
                  <>
                    <div className="filter-option-item">
                      <div className="filter-option-content">
                        <Text className="filter-option-text">
                          {t('signalFix.allCategories')} ({availableSegnaliTypes.length})
                        </Text>
                      </div>
                      <Switch
                        checked={Object.values(segnaliFilterOptions).every((v) => v)}
                        onChange={(checked) =>
                          checked ? handleSelectAll('segnali') : handleDeselectAll('segnali')
                        }
                        className="filter-switch"
                      />
                    </div>

                    {typesMatchingSearch(availableSegnaliTypes, 'segnali').map((type) => {
                      const typeData = getSignTypeData(type, 'segnali');
                      if (!typeData) return null;
                      return (
                        <div key={type} className="filter-option-item">
                          <div className="filter-option-content">
                            <span className="filter-option-icon">
                              {typeData.iconUrl ? (
                                <img
                                  src={typeData.iconUrl}
                                  alt={typeData.value}
                                  style={{
                                    width: 18,
                                    height: 18,
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                "🚦"
                              )}
                            </span>
                            <Text className="filter-option-text">
                              {typeData.value}{" "}
                              <span className="filter-count">
                                {typeData.count}
                              </span>
                            </Text>
                          </div>
                          <Switch
                            checked={segnaliFilterOptions[type] || false}
                            onChange={(checked) =>
                              handleFilterToggle(type, checked, 'segnali')
                            }
                            className="filter-switch"
                          />
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div className="filter-option-item">
                      <div className="filter-option-content">
                        <Text className="filter-option-text">
                          {t('signalFix.allCategories')} ({availableSegnaleticaTypes.length})
                        </Text>
                      </div>
                      <Switch
                        checked={Object.values(segnaleticaFilterOptions).every((v) => v)}
                        onChange={(checked) =>
                          checked ? handleSelectAll('segnaletica') : handleDeselectAll('segnaletica')
                        }
                        className="filter-switch"
                      />
                    </div>

                    {typesMatchingSearch(availableSegnaleticaTypes, 'segnaletica').map((type) => {
                      const typeData = getSignTypeData(type, 'segnaletica');
                      if (!typeData) return null;
                      return (
                        <div key={type} className="filter-option-item">
                          <div className="filter-option-content">
                            <span className="filter-option-icon">
                              {typeData.iconUrl ? (
                                <img
                                  src={typeData.iconUrl}
                                  alt={typeData.value}
                                  style={{
                                    width: 18,
                                    height: 18,
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                "🚦"
                              )}
                            </span>
                            <Text className="filter-option-text">
                              {typeData.value}{" "}
                              <span className="filter-count">
                                {typeData.count}
                              </span>
                            </Text>
                          </div>
                          <Switch
                            checked={segnaleticaFilterOptions[type] || false}
                            onChange={(checked) =>
                              handleFilterToggle(type, checked, 'segnaletica')
                            }
                            className="filter-switch"
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Right side: Buttons */}
      <div className="signal-fix-toolbar-right">
        <Space>
          <div style={{ position: "relative" }}>
            <Button
              icon={<MdOutlineDirectionsCar />}
              type={showCarPath ? "primary" : "default"}
              className="car-road"
              onClick={() => {
                if (onToggleCarPath) {
                  onToggleCarPath();
                }
                setShowCarPathCard(!showCarPathCard);
              }}
            />

            {showCarPathCard && (
              <Card
                bodyStyle={{ padding: 0 }}
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: "420px",
                  zIndex: 1000,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  height: "fit-content",
                }}
              >
                {/* Card Title */}
                <div
                  style={{
                    padding: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Title level={4} style={{ margin: 0 }}>
                    <b>{t('signalFix.carPath')}</b>
                  </Title>
                  <button
                    type="button"
                    className="filter-card-close"
                    aria-label="Chiudi"
                    onClick={() => {
                      setShowCarPathCard(false);
                      if (onToggleCarPath && showCarPath) {
                        onToggleCarPath();
                      }
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Card Content */}
                <div style={{ padding: "24px" }}>
                  <Row justify={"space-between"} align={"middle"}>
                    <Col>
                      <Row gutter={12}>
                        <Col>
                          <img src={PositionIcon} alt="position icon"></img></Col>
                        <Col>
                          <span style={{ color: "rgba(176, 176, 176, 1)" }}>{t('signalFix.region')}</span>
                          <h3>
                            {locationLoading ? (
                              <Spin size="small" />
                            ) : (
                              carPathLocation || "N/A"
                            )}
                          </h3>
                        </Col>
                      </Row>
                      <Row style={{ marginTop: "12px" }} gutter={12}>
                        <Col>
                          <img src={LengthIcon} alt="length icon"></img></Col>
                        <Col>
                          <span style={{ color: "rgba(176, 176, 176, 1)" }}>{t('signalFix.kmTraveled')}</span>
                          <h3>
                            {carPathDistance !== null 
                              ? `${carPathDistance.toFixed(2)} km` 
                              : "N/A"}
                          </h3>
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <img src={CarIcon} alt="car image" />
                    </Col>
                  </Row>
                </div>
              </Card>
            )}
          </div>

          <Button
            icon={<PlusOutlined />}
            type="default"
            onClick={() => setAddNew && setAddNew(true)}
            className="signal-fix-add-button"
          >
            {t('signalFix.addButton')}
          </Button>
          <Button
            icon={<MdOutlineFileDownload />}
            type="default"
            onClick={handleExcelDownload}
            loading={exportLoading}
            disabled={exportLoading}
            className="signal-fix-add-button"
          >
            {exportLoading ? t('signalFix.exporting') : t('signalFix.downloadButton')}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default SignalFixExtras;
